import { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { AnalystChartConfig } from './agenticTypes';
import { buildChartOption } from './chartOptions';

/* Brand chart hexes -> calm muted palette (dark only); series stay readable and grid/scaffold hexes flip to dark tokens. */
const DARK_CHART_COLOR_MAP: Record<string, string> = {
  '#00AEEF': '#56b6c2',
  '#D4007F': '#e57ba3',
  '#003A79': '#58a6ff',
  '#7FC8EC': '#6fa8d6',
  '#E58BBE': '#d49ac0',
  '#6B8FB5': '#7f9bbd',
  '#F5A623': '#d9a441',
  '#E8EDF2': '#252B32',
  '#EBF8FF': '#1b2733',
  '#F1F5F9': '#21262d',
  '#E2E8F0': '#252B32',
  '#E0F2FE': '#21262d',
};

/** Pre-compiled [regex, replacement] pairs so each colour is matched once. */
const DARK_CHART_COLOR_RULES = Object.entries(DARK_CHART_COLOR_MAP).map(
  ([from, to]) => [new RegExp(from, 'gi'), to] as const
);

/** Recursively replace known brand hexes in any string within the ECharts option. */
function remapColors(value: unknown): unknown {
  if (typeof value === 'string') {
    return DARK_CHART_COLOR_RULES.reduce((out, [re, to]) => out.replace(re, to), value);
  }
  if (Array.isArray(value)) return value.map(remapColors);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [key, remapColors(val)])
    );
  }
  return value;
}

/** Apply the dark palette + dark axis/tooltip scaffolding to a built option. */
function toDarkOption(option: Record<string, any>): Record<string, any> {
  const remapped = remapColors(option) as Record<string, any>;
  const darkTooltip = {
    ...remapped.tooltip,
    backgroundColor: '#161b22',
    borderColor: '#252B32',
    textStyle: { color: '#f5f5f5' },
  };
  return {
    ...remapped,
    textStyle: { ...remapped.textStyle, color: '#c4c4c4' },
    tooltip: Array.isArray(remapped.tooltip) ? remapped.tooltip : darkTooltip,
  };
}

interface AnalystChartProps {
  readonly config: AnalystChartConfig;
  readonly height?: number;
}

const SCALE_TIERS = [
  { limit: 1e3, label: 'K = Thousand' },
  { limit: 1e6, label: 'M = Million' },
  { limit: 1e9, label: 'B = Billion' },
  { limit: 1e12, label: 'T = Trillion' },
] as const;

/** True when the built option has at least one numeric (`type: 'value'`) axis. */
function hasValueAxis(option: Record<string, any>): boolean {
  const axes = [option.xAxis, option.yAxis].flat().filter(Boolean);
  return axes.some((axis: any) => axis?.type === 'value');
}

/**
 * Count leaves in a hierarchy chart payload (treemap / decomp_tree). Used to
 * size the canvas so deep trees have enough room when the user expands them
 * (the tree renders collapsed to the first level by default; `roam` handles the
 * rest). Caps recursion at a sensible depth to keep the reserved height bounded.
 */
function countTreeLeaves(data: unknown, maxDepth = 2): number {
  if (!Array.isArray(data)) return 0;
  let leaves = 0;
  const walk = (node: unknown, depth: number) => {
    if (!node || typeof node !== 'object') return;
    const children = (node as { children?: unknown[] }).children;
    if (depth >= maxDepth || !Array.isArray(children) || children.length === 0) {
      leaves += 1;
      return;
    }
    children.forEach((child) => walk(child, depth + 1));
  };
  data.forEach((node) => walk(node, 0));
  return leaves || data.length;
}

/**
 * Apply controlled `collapsed` flags (keyed by node `id`) onto a built tree
 * option, in place. Nodes without an override keep the default the option was
 * built with (root expanded, everything below the first level collapsed).
 */
function applyTreeCollapse(
  option: Record<string, any>,
  collapsedById: Record<string, boolean>
): void {
  const series = option?.series?.[0];
  if (!series || !Array.isArray(series.data)) return;
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    if (hasChildren && typeof node.id === 'string' && node.id in collapsedById) {
      node.collapsed = collapsedById[node.id];
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  series.data.forEach(walk);
}

/**
 * Inject dark-mode content1 surface colours onto the tree label. Canvas text
 * can't read CSS vars, so the light values live in chartOptions and the dark
 * equivalents (content1 `#161b22` / foreground `#f5f5f5` / divider) are set here.
 */
function applyTreeDarkSurface(option: Record<string, any>): void {
  const label = option?.series?.[0]?.label;
  if (!label) return;
  label.color = '#f5f5f5';
  label.backgroundColor = '#161b22';
  label.borderColor = '#30363d';
}

/**
 * Compute the next controlled-collapse map when a tree node is clicked. When
 * space is tight, expanding a first-level branch collapses its siblings so the
 * opened branch has room (accordion behaviour).
 */
function computeTreeToggle(
  previous: Record<string, boolean>,
  nodeId: string,
  depth: number,
  firstLevelIds: string[],
  spaceLimited: boolean
): Record<string, boolean> {
  const currentlyCollapsed = previous[nodeId] ?? false;
  const nextCollapsed = !currentlyCollapsed;
  const next: Record<string, boolean> = { ...previous, [nodeId]: nextCollapsed };
  if (!nextCollapsed && depth === 1 && spaceLimited) {
    for (const siblingId of firstLevelIds) {
      if (siblingId !== nodeId) next[siblingId] = true;
    }
  }
  return next;
}

/** Largest absolute numeric value across all rows — drives which suffixes apply. */
function maxAbsValue(data: unknown): number {
  if (!Array.isArray(data)) return 0;
  let max = 0;
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    for (const raw of Object.values(row as Record<string, unknown>)) {
      const num = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(num)) max = Math.max(max, Math.abs(num));
    }
  }
  return max;
}

/** ECharts-backed renderer for the 17 graphical chart types (contract §5). */
export function AnalystChart({ config, height = 300 }: AnalystChartProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const chartRef = useRef<ReactECharts | null>(null);

  // Controlled expand/collapse for decomp_tree, keyed by node id; empty = defaults.
  const [treeCollapsed, setTreeCollapsed] = useState<Record<string, boolean>>({});

  // Some chart types need more vertical room than the default 300px:
  //  - hbar: each category bar needs ~28px.
  //  - decomp_tree: the tree fans out vertically — count visible leaves.
  //  - treemap: needs at least ~360px to make multi-level nesting readable.
  const resolvedHeight = useMemo(() => {
    if (config.chartType === 'hbar') {
      const rowCount = Array.isArray(config.data) ? config.data.length : 0;
      return Math.max(height, rowCount * 28 + 96);
    }
    if (config.chartType === 'decomp_tree') {
      const leafCount = countTreeLeaves(config.data, 3);
      return Math.max(height, Math.min(550, leafCount * 46 + 140));
    }
    if (config.chartType === 'treemap') {
      return Math.max(height, 420);
    }
    return height;
  }, [config.chartType, config.data, height]);

  const treeMeta = useMemo(() => {
    if (config.chartType !== 'decomp_tree') return null;
    const rows = Array.isArray(config.data) ? config.data : [];
    const singleRoot = rows.length === 1;
    const branches = (singleRoot ? (rows[0] as any)?.children ?? [] : rows) as unknown[];
    const firstLevelIds = branches.map((_, index) => `r/${index}`);
    return { firstLevelIds, leaves: countTreeLeaves(config.data, 3) };
  }, [config.chartType, config.data]);

  // When the fully-expanded tree can't fit, expanding one branch collapses siblings.
  const treeSpaceLimited = useMemo(
    () => (treeMeta ? treeMeta.leaves * 58 > resolvedHeight : false),
    [treeMeta, resolvedHeight]
  );

  useEffect(() => {
    setTreeCollapsed({});
  }, [config.chartType, config.data]);

  const option = useMemo(() => {
    const built = buildChartOption(config);
    if (!built) return built;
    const themed = isDark ? toDarkOption(built) : built;
    if (config.chartType === 'decomp_tree') {
      applyTreeCollapse(themed, treeCollapsed);
      if (isDark) applyTreeDarkSurface(themed);
    }
    return themed;
  }, [config, isDark, treeCollapsed]);

  const onEvents = useMemo(
    () => ({
      click: (params: any) => {
        if (config.chartType !== 'decomp_tree' || !treeMeta) return;
        const data = params?.data;
        const hasChildren = data && Array.isArray(data.children) && data.children.length > 0;
        if (!hasChildren || typeof data.id !== 'string') return;
        const nodeId: string = data.id;
        const depth = (Array.isArray(params.treeAncestors) ? params.treeAncestors.length : 1) - 1;
        chartRef.current?.getEchartsInstance()?.dispatchAction({ type: 'downplay' });
        setTreeCollapsed((previous) =>
          computeTreeToggle(previous, nodeId, depth, treeMeta.firstLevelIds, treeSpaceLimited)
        );
      },
      globalout: () => {
        if (config.chartType !== 'decomp_tree') return;
        chartRef.current?.getEchartsInstance()?.dispatchAction({ type: 'downplay' });
      },
    }),
    [config.chartType, treeMeta, treeSpaceLimited]
  );

  // Only show the K/M/B/T key when the axis actually uses compact suffixes
  // (a value axis exists and the data is large enough to be abbreviated).
  const scaleHint = useMemo(() => {
    if (!option || !hasValueAxis(option)) return '';
    const max = maxAbsValue(config.data);
    const applicable = SCALE_TIERS.filter((tier) => max >= tier.limit);
    return applicable.map((tier) => tier.label).join('  ·  ');
  }, [option, config.data]);

  if (!option) {
    return (
      <div className="rounded-md border border-default-200 bg-content1 p-3 text-sm text-default-500">
        {t('Unable to render this chart type')}
      </div>
    );
  }

  const handleDownload = () => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    const url = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
    const link = document.createElement('a');
    link.href = url;
    link.download = `${config.chartType}-chart.png`;
    link.click();
  };

  // Re-apply the option with notMerge to reset zoom/pan to the fitted layout.
  const handleFitToView = () => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance || !option) return;
    instance.setOption(option, { notMerge: true });
  };

  return (
    <div className="rounded-md border border-default-200 bg-content1 p-2">
      <div className="flex items-center justify-end gap-2">
        {scaleHint ? (
          <span className="text-[10px] leading-tight text-default-400">
            {t('Scale')}: {scaleHint}
          </span>
        ) : null}
        {config.chartType === 'decomp_tree' ? (
          <button
            type="button"
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-default-500 hover:bg-default-100"
            aria-label={t('Fit to view')}
            title={t('Fit to view')}
            onClick={handleFitToView}
          >
            <Icon icon="tabler:focus-centered" width={18} />
          </button>
        ) : null}
        <details className="relative">
          <summary
            className="inline-flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-md text-default-500 hover:bg-default-100"
            aria-label={t('More actions')}
          >
            <Icon icon="tabler:dots-vertical" width={18} />
          </summary>
          <div className="absolute right-0 z-20 mt-1 w-36 rounded-md border border-default-200 bg-content1 p-1 shadow-md">
            <button
              type="button"
              className="w-full rounded px-2 py-1.5 text-left text-sm text-default-700 hover:bg-default-100"
              onClick={handleDownload}
            >
              {t('Save as PNG')}
            </button>
          </div>
        </details>
      </div>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: resolvedHeight, width: '100%' }}
        notMerge
        lazyUpdate
        onEvents={onEvents}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}

export default AnalystChart;
